import { useTheme } from '@mui/material';
import cn from 'classnames';
import * as React from 'react';
import { ReactElement, useCallback, useMemo } from 'react';

import { Drawer, Loader, SendItemsForm } from '../../components';
import useWindowSize from '../../hooks/useWindowSize';
import { IChangeBucketItemPayload } from '../../types/bucket';
import { ICampaign, ICampaignItem, ICustomSend } from '../../types/campaigns';
import { IOnDemandItem } from '../../types/inventories';
import { IReportWithCampaignSummaries } from '../../types/reports';
import { BoxDetailsSidebarModeEnum, DrawerAnimationDirectionEnum } from '../../types/shell';
import { isItemDigital } from '../../utils/inventories';
import { SidebarFooter, SidebarHeader } from './components';

import styles from './BoxDetailsSidebar.module.scss';

interface IProps {
  onClose: () => void;
  onSubmit?: (box: ICampaign | ICustomSend) => void;
  onChangeItem?: (itemData: IChangeBucketItemPayload) => void;
  onRemoveItem?: (item: ICampaignItem | IOnDemandItem) => void;
  onCustomize?: (item: ICampaign) => void;
  onReset?: () => void;
  isBoxSelected?: boolean;
  isLoading?: boolean;
  isPYGEnabled?: boolean;
  box?: ICampaign | ICustomSend | IReportWithCampaignSummaries;
  mode?: BoxDetailsSidebarModeEnum;
  actionButtons?: ReactElement[];
  title?: string;
  isDirectSend?: boolean;
}

const BoxDetailsSidebar: React.FC<IProps> = ({
  onClose,
  box,
  isPYGEnabled,
  isLoading,
  mode,
  onChangeItem,
  onRemoveItem,
  onCustomize,
  onReset,
  actionButtons,
  title,
  isDirectSend,
}: IProps) => {
  const { sass: variables } = useTheme();
  const { mobile, HD } = useWindowSize();
  const { items = [], name = title || '' } = useMemo(
    () => box || ({} as ICampaign | ICustomSend | IReportWithCampaignSummaries),
    [box],
  );
  const isDigital = useMemo(() => items?.every(isItemDigital), [items]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const from = useMemo(
    () => (mobile ? variables.boxSidebarMinHeight : variables.boxSidebarMinWidth),
    [variables.boxSidebarMinHeight, variables.boxSidebarMinWidth, mobile],
  );

  const to = useMemo(() => {
    if (mobile) {
      return variables.boxSidebarMaxHeight;
    }

    if (HD) {
      return variables.boxSidebarMaxWidthHD;
    }

    return variables.boxSidebarMaxWidthFullHD;
  }, [variables.boxSidebarMaxHeight, variables.boxSidebarMaxWidthHD, variables.boxSidebarMaxWidthFullHD, mobile, HD]);

  const trigger = useMemo(() => !!box, [box]);

  const handleEditSendClick = useCallback(() => {
    if (box) {
      onCustomize?.(box as ICampaign);
    }
  }, [box, onCustomize]);

  const isEditSendButtonVisible = useMemo(
    () => onCustomize && !isDigital && !isPYGEnabled,
    [onCustomize, isDigital, isPYGEnabled],
  );

  const heading = useMemo(() => {
    return name ? name : title;
  }, [name, title]);

  return (
    <Drawer
      className={cn(styles.container)}
      direction={mobile ? DrawerAnimationDirectionEnum.Vertically : DrawerAnimationDirectionEnum.Horizontally}
      from={from}
      to={to}
      trigger={trigger}
      onClose={handleClose}
      withOverlay
      overlayClassName={styles.overlay}
    >
      {trigger && (
        <div className={styles.detailsContainer}>
          <Loader isLoading={isLoading} />
          <SidebarHeader title={heading || ''} onClose={handleClose} items={items as ICampaignItem[]} />
          <SendItemsForm mode={mode} items={items} onChange={onChangeItem} onRemove={onRemoveItem} />
          <SidebarFooter
            items={items as ICampaignItem[]}
            isEditSendButtonVisible={isEditSendButtonVisible}
            onReset={onReset}
            isDirectSend={isDirectSend}
            handleEditSendClick={handleEditSendClick}
          >
            {actionButtons}
          </SidebarFooter>
        </div>
      )}
    </Drawer>
  );
};

export default BoxDetailsSidebar;
